const TENOR_MULTIPLIERS = {
  1: 1.0,
  2: 1.02,
  3: 1.04,
  4: 1.06,
  5: 1.08,
  6: 1.1,
  7: 1.12,
  8: 1.14,
  9: 1.16,
  10: 1.18,
  11: 1.2,
  12: 1.22,
};

const DEFAULT_SPREADS = {
  EURUSD: 0.00001,
  USDJPY: 0.000012,
  GBPUSD: 0.000012,
  AUDUSD: 0.000015,
  USDCAD: 0.000015,
  USDCHF: 0.000015,
  USDHKD: 0.00002,
  USDCNY: 0.000025,
  USDINR: 0.005,
  EURINR: 0.006,
  GBPINR: 0.006,
  AUDINR: 0.006,
  JPYINR: 0.006,
  CNYINR: 0.006,
  CHFINR: 0.006,
  CADINR: 0.006,
};

const isInrQuotedPair = (pair) => String(pair || "").endsWith("INR");

const toOutputHalfSpread = (pair, halfSpreadRate, divisor = 100) =>
  Number(halfSpreadRate) * Number(divisor || 1);

/** SourceHalfSpread = BaseHalfSpread × TenorMultiplier (no PremiumFactor). */
const computeHalfSpread = (baseHalfSpread, tenorMonth) => {
  const base = Number(baseHalfSpread);
  const multiplier = TENOR_MULTIPLIERS[Number(tenorMonth)] ?? 1;
  return base * multiplier;
};

const getBaseHalfSpread = (pair, spreadConfig = {}) => {
  const spread =
    spreadConfig[pair]?.base_half_spread ??
    DEFAULT_SPREADS[pair] ??
    0.000015;
  if (pair === "USDINR" && spread < 0.001) {
    return DEFAULT_SPREADS.USDINR;
  }
  return spread;
};

const formatPremium = (pair, value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(isInrQuotedPair(pair) ? 4 : 6);
};

const deriveMidFromBidAsk = (bid, ask) => {
  const b = Number(bid);
  const a = Number(ask);
  if (!Number.isFinite(b) || !Number.isFinite(a)) return "";
  return ((b + a) / 2).toString();
};

const interpolateTenorValue = (lowerTenor, lowerValue, upperTenor, upperValue, targetTenor) => {
  const lo = Number(lowerTenor);
  const hi = Number(upperTenor);
  const target = Number(targetTenor);
  const v1 = Number(lowerValue);
  const v2 = Number(upperValue);
  if (![lo, hi, target, v1, v2].every(Number.isFinite) || hi === lo) return null;
  return v1 + (v2 - v1) * ((target - lo) / (hi - lo));
};

/**
 * Fill any missing / 0 MID tenors via linear interpolation between nearest
 * known anchors (e.g. 1M+12M → 2M…11M; 6M+9M → 7M/8M).
 * Matches FX_Forward_Missing_Tenor_Interpolation_Guide.txt formula.
 */
const fillMissingTenors = (tenorsByMonth) => {
  const byMonth = new Map();
  for (const row of tenorsByMonth || []) {
    byMonth.set(Number(row.tenor_month), { ...row });
  }

  const getMid = (month) => {
    const row = byMonth.get(month);
    if (!row) return null;
    if (row.mid_premium !== "" && row.mid_premium != null) {
      const mid = Number(row.mid_premium);
      // 0 is treated as missing (empty placeholder), not a quoted premium
      if (Number.isFinite(mid) && mid !== 0) return mid;
    }
    if (row.mid_points != null && row.mid_points !== "") {
      const mid = Number(row.mid_points);
      if (Number.isFinite(mid) && mid !== 0) return mid;
    }
    const bid = Number(row.bid_premium);
    const ask = Number(row.ask_premium);
    if (Number.isFinite(bid) && Number.isFinite(ask) && !(bid === 0 && ask === 0)) {
      return (bid + ask) / 2;
    }
    return null;
  };

  const pairCode =
    tenorsByMonth?.find((row) => row.pair)?.pair || "USDINR";

  const setMid = (month, mid) => {
    const row = byMonth.get(month) || { tenor_month: month };
    byMonth.set(month, {
      ...row,
      tenor_month: month,
      mid_premium: formatPremium(pairCode, mid),
      interpolated: true,
    });
  };

  const anchors = [];
  for (let m = 1; m <= 12; m += 1) {
    if (getMid(m) != null) anchors.push(m);
  }

  for (let i = 0; i < anchors.length - 1; i += 1) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    const vLo = getMid(lower);
    const vHi = getMid(upper);
    if (vLo == null || vHi == null) continue;
    for (let m = lower + 1; m < upper; m += 1) {
      if (getMid(m) == null) {
        const interpolated = interpolateTenorValue(lower, vLo, upper, vHi, m);
        if (interpolated != null) setMid(m, interpolated);
      }
    }
  }

  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return (
      byMonth.get(month) || {
        tenor_month: month,
        mid_premium: "",
        bid_premium: "",
        ask_premium: "",
        settlement_date: "",
      }
    );
  });
};

const isQuotedMidValue = (value) => {
  if (value === "" || value == null) return false;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
};

const hasValidMid = (row) =>
  isQuotedMidValue(row.mid_premium) || isQuotedMidValue(row.mid_points);

/**
 * Given tenor rows with mid_premium, compute bid/ask for entire curve.
 * HalfSpread = BaseHalfSpread × TenorMultiplier (no PremiumFactor).
 */
const buildBidAskFromMids = (pair, tenors, spreadConfig = {}, divisor = 100) => {
  const withFilledMids = fillMissingTenors(
    tenors.map((row) => ({ ...row, pair }))
  );
  const baseHalfSpread = getBaseHalfSpread(pair, spreadConfig);
  const quotedRows = withFilledMids.filter(hasValidMid);
  if (!quotedRows.length) return withFilledMids;

  return withFilledMids.map((row) => {
    if (!hasValidMid(row)) {
      return {
        ...row,
        bid_premium: "",
        ask_premium: "",
      };
    }

    const mid = Number(row.mid_premium);
    const halfSpreadRate = computeHalfSpread(baseHalfSpread, row.tenor_month);
    const halfSpread = toOutputHalfSpread(pair, halfSpreadRate, divisor);
    const bid = mid - halfSpread;
    const ask = mid + halfSpread;

    return {
      ...row,
      mid_premium: formatPremium(pair, mid),
      bid_premium: formatPremium(pair, bid),
      ask_premium: formatPremium(pair, ask),
    };
  });
};

export {
  TENOR_MULTIPLIERS,
  DEFAULT_SPREADS,
  buildBidAskFromMids,
  computeHalfSpread,
  deriveMidFromBidAsk,
  fillMissingTenors,
  formatPremium,
  getBaseHalfSpread,
  interpolateTenorValue,
  isInrQuotedPair,
  isQuotedMidValue,
};
